import React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PlusIcon,
  PencilIcon,
  XMarkIcon,
  CogIcon,
  PowerIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  CreditCardIcon,
  UsersIcon,
  MegaphoneIcon,
  BellIcon,
  ChartBarIcon,
  BanknotesIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  TrashIcon,
  TagIcon,
  CloudArrowDownIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { getBackendOrigin, getImageUrl } from '@/utils/imagePaths';
import { getAppSocket } from '../lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';
import { hoyLocal, toYMD } from '../utils/fechaLocal';
import PaymentConditionsSettings from '../components/PaymentConditionsSettings';
import VisionDatasetSettings from '../components/VisionDatasetSettings';

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
}

interface UserForm {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  role: string;
}



// Company info types
interface CompanyInfoForm {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  cuit?: string;
  iibb?: string;
  taxStatus?: string;
  activityStartDate?: string;
  whatsapp?: string;
  instagram?: string;
  weekdayHours?: string;
  saturdayHours?: string;
}

interface MessageTemplate {
  name: string;
  description: string;
  template: string;
  variables: string[];
  enabled?: boolean;
}

interface MessageTemplates {
  [key: string]: MessageTemplate;
}

interface NotificationConfig {
  id: number;
  type: string;
  name: string;
  description: string;
  enabled: boolean;
  channels: string[];
  schedule: string;
  recipients: string[];
  conditions: any;
  created_at: string;
  updated_at: string;
}

interface NotificationForm {
  type: string;
  name: string;
  description: string;
  enabled: boolean;
  channels: string[];
  schedule: string;
  recipients: string[];
  conditions: any;
}

interface EmailConfig {
  provider: 'custom';
  smtpHost?: string;
  smtpPort?: number;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
  useTLS: boolean;
  enabled: boolean;
  bccDefault?: string;
  replyToDefault?: string;
}

interface EmailConfigForm {
  provider?: 'custom';
  smtpHost?: string;
  smtpPort?: number;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
  useTLS: boolean;
  enabled: boolean;
  bccDefault?: string;
  replyToDefault?: string;
}

interface AfipConfig {
  cuitEmisor?: string;
  puntoVenta?: string;
  wsmtxcaWsdlHomologacion?: string;
  wsmtxcaWsdlProduccion?: string;
  environment?: 'homologacion' | 'produccion';
  enabled: boolean;
  hasCert?: boolean;
  hasKey?: boolean;
  preferredWs?: 'wsfe' | 'wsmtxca';
}

interface AfipConfigForm {
  cuitEmisor: string;
  puntoVenta: string;
  wsmtxcaWsdlHomologacion?: string;
  wsmtxcaWsdlProduccion?: string;
  environment: 'homologacion' | 'produccion';
  preferredWs: 'wsfe' | 'wsmtxca';
  cert?: FileList;
  key?: FileList;
  enabled: boolean;
}

interface DailyReportConfig {
  enabled: boolean;
  email: string;
  phone: string;
  send_time: string;
  saturday_send_time: string;
  include_cash_summary: boolean;
  include_services: boolean;
  include_products: boolean;
  include_appointments: boolean;
  include_budgets: boolean;
}

interface PaymentMethod {
  id: string;
  name: string;
  code: string;
  description?: string;
  surcharge: number;
  enabled: boolean;
  isDefault: boolean;
}

interface PaymentMethodForm {
  name: string;
  code: string;
  description: string;
  surcharge: number;
  enabled: boolean;
  isDefault: boolean;
}

interface CustomDiscount {
  id?: string;
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_amount: number;
  max_discount?: number;
  applies_to: 'all' | 'products' | 'services' | 'specific_product' | 'specific_brand' | 'specific_category';
  target_id?: string;
  target_name?: string;
  start_date: string;
  end_date: string;
  active_days: string[];
  active_hours: string[];
  max_uses?: number;
  current_uses: number;
  enabled: boolean;
  priority: number;
}

interface CustomDiscountForm {
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_amount: number;
  max_discount: number;
  applies_to: 'all' | 'products' | 'services' | 'specific_product' | 'specific_brand' | 'specific_category';
  target_id: string;
  target_name: string;
  start_date: string;
  end_date: string;
  active_days: string[];
  active_hours: string[];
  max_uses: number;
  priority: number;
}

interface Coupon {
  id?: string;
  code: string;
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_amount: number;
  max_discount?: number;
  applies_to: 'all' | 'products' | 'services' | 'specific_product' | 'specific_brand' | 'specific_category';
  target_id?: string;
  target_name?: string;
  start_date: string;
  end_date: string;
  max_uses?: number;
  current_uses: number;
  max_uses_per_client?: number;
  enabled: boolean;
  status?: 'active' | 'expired' | 'exhausted' | 'disabled';
  total_usage_count?: number;
}

interface CouponForm {
  code: string;
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_amount: number;
  max_discount: number;
  applies_to: 'all' | 'products' | 'services' | 'specific_product' | 'specific_brand' | 'specific_category';
  target_id: string;
  target_name: string;
  start_date: string;
  end_date: string;
  max_uses: number;
  max_uses_per_client: number;
}

type CompanyArea = {
  id: string;
  name: string;
  isActive?: number;
};

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [waStatus, setWaStatus] = useState<'connected' | 'disconnected' | 'connecting' | 'unknown'>('unknown');
  const [waLoading, setWaLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyLogoFile, setCompanyLogoFile] = useState<File | null>(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState<string | null>(null);
  const [companyAreas, setCompanyAreas] = useState<CompanyArea[]>([]);
  const [companyAreasLoading, setCompanyAreasLoading] = useState(false);
  const [companyAreaSaving, setCompanyAreaSaving] = useState(false);
  const [companyAreaDeletingId, setCompanyAreaDeletingId] = useState<string | null>(null);
  const [companyNewArea, setCompanyNewArea] = useState('');

  const [supplyCategories, setSupplyCategories] = useState<{id: string; name: string}[]>([]);
  const [supplyCategoriesLoading, setSupplyCategoriesLoading] = useState(false);
  const [supplyCategorySaving, setSupplyCategorySaving] = useState(false);
  const [supplyCategoryDeletingId, setSupplyCategoryDeletingId] = useState<string | null>(null);
  const [newSupplyCategory, setNewSupplyCategory] = useState('');

  const [observationTypes, setObservationTypes] = useState<{id: string; name: string; isActive: number}[]>([]);
  const [observationTypesLoading, setObservationTypesLoading] = useState(false);
  const [observationTypeSaving, setObservationTypeSaving] = useState(false);
  const [observationTypeDeletingId, setObservationTypeDeletingId] = useState<string | null>(null);
  const [newObservationType, setNewObservationType] = useState('');

  // Section toggles (módulos)
  const [sectionToggles, setSectionToggles] = useState<{key: string; label: string; description: string; enabled: number}[]>([]);
  const rolesCount = React.useMemo(
    () => Array.from(new Set(users.map(u => u.role).filter(Boolean))).length,
    [users]
  );
  const [skillsCount, setSkillsCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    axios.get('/api/skills/catalog')
      .then((res) => { if (!cancelled) setSkillsCount((res.data?.skills || []).filter((s: any) => s.isActive !== false).length); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const {
    register: registerCompany,
    handleSubmit: handleSubmitCompany,
    setValue: setCompanyValue,
    formState: { errors: companyErrors }
  } = useForm<CompanyInfoForm>();

  const { 
    register: registerNotification, 
    handleSubmit: handleSubmitNotification, 
    reset: resetNotification, 
    formState: { errors: notificationErrors } 
  } = useForm<NotificationForm>();

  // Estados para plantillas de mensajes
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplates>({});
  const [showMessageTemplatesModal, setShowMessageTemplatesModal] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [activeTab, setActiveTab] = useState<'whatsapp' | 'email'>('whatsapp');
  const [activeTextareaRef, setActiveTextareaRef] = useState<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<{key: string, template: any, type: 'whatsapp' | 'email'} | null>(null);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<{[key: string]: {
    name: string;
    description: string;
    subject: string;
    body: string;
    variables: string[];
    enabled?: boolean;
  }}>({});

  // Estados para reportes diarios
  const [dailyReportConfig, setDailyReportConfig] = useState<DailyReportConfig>({
    enabled: false,
    email: '',
    phone: '',
    send_time: '19:00',
    saturday_send_time: '13:00',
    include_cash_summary: true,
    include_services: true,
    include_products: true,
    include_appointments: true,
    include_budgets: true
  });

  // Estados para metodos de pago
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  
  // Estados para descuentos personalizados
  const [customDiscounts, setCustomDiscounts] = useState<CustomDiscount[]>([]);
  const [showCustomDiscountModal, setShowCustomDiscountModal] = useState(false);
  const [editingCustomDiscount, setEditingCustomDiscount] = useState<CustomDiscount | null>(null);
  const [loadingCustomDiscounts, setLoadingCustomDiscounts] = useState(false);

  // Estados para cupones
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [showCouponsModal, setShowCouponsModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [loadingCoupons, setLoadingCoupons] = useState(false);

  const { 
    register: registerCustomDiscount,
    handleSubmit: handleSubmitCustomDiscount,
    reset: resetCustomDiscount,
    setValue: setCustomDiscountValue,
    control: customDiscountControl,
    formState: { errors: customDiscountErrors }
  } = useForm<CustomDiscountForm>({
    defaultValues: {
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      min_amount: 0,
      max_discount: 0,
      applies_to: 'all',
      target_id: '',
      target_name: '',
      start_date: hoyLocal(),
      end_date: toYMD(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
      active_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      active_hours: ['09:00-18:00'],
      max_uses: 100,
      priority: 0
    }
  });

  // Formulario para cupones
  const { 
    register: registerCoupon,
    handleSubmit: handleSubmitCoupon,
    reset: resetCoupon,
    setValue: setCouponValue,
    control: couponControl,
    formState: { errors: couponErrors }
  } = useForm<CouponForm>({
    defaultValues: {
      code: '',
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      min_amount: 0,
      max_discount: 0,
      applies_to: 'all',
      target_id: '',
      target_name: '',
      start_date: hoyLocal(),
      end_date: toYMD(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
      max_uses: 100,
      max_uses_per_client: 1
    }
  });

  const [showDailyReportModal, setShowDailyReportModal] = useState(false);
  const [dailyReportLoading, setDailyReportLoading] = useState(false);

  // Estados para notificaciones
  const [notifications, setNotifications] = useState<NotificationConfig[]>([]);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationConfig | null>(null);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [emailConfig, setEmailConfig] = useState<EmailConfig | null>(null);
  // Estados para Configuración de AFIP (WSMTXCA)
  const [afipConfig, setAfipConfig] = useState<AfipConfig | null>(null);
  const [showAfipConfigModal, setShowAfipConfigModal] = useState(false);
  const [afipConfigLoading, setAfipConfigLoading] = useState(false);
  const {
    register: registerAfipConfig,
    handleSubmit: handleSubmitAfipConfig,
    reset: resetAfipConfig,
    setValue: setAfipConfigValue,
    watch: watchAfipConfig,
    formState: { errors: afipConfigErrors }
  } = useForm<AfipConfigForm>({
    defaultValues: {
      cuitEmisor: '',
      puntoVenta: '',
      wsmtxcaWsdlHomologacion: '',
      wsmtxcaWsdlProduccion: '',
      environment: 'homologacion',
      enabled: false
    }
  });

  // Estados para configuración de Kapso WhatsApp
  const [kapsoConfig, setKapsoConfig] = useState<{ hasApiKey: boolean; hasPhoneNumberId: boolean; phoneNumberId?: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<number | null>(null);
  const [approvedBudgets, setApprovedBudgets] = useState<any[]>([]);


  // Consultar estado de WhatsApp al montar
  const fetchSectionToggles = useCallback(async () => {
    try {
      const res = await axios.get('/api/settings/section-toggles');
      setSectionToggles(res.data || []);
    } catch {}
  }, []);

  const handleToggleSection = useCallback(async (key: string, enabled: boolean) => {
    try {
      await axios.put('/api/settings/section-toggles', { key, enabled });
      setSectionToggles(prev => prev.map(t => t.key === key ? { ...t, enabled: enabled ? 1 : 0 } : t));
      toast.success(`Módulo ${enabled ? 'activado' : 'desactivado'}`);
    } catch {
      toast.error('Error al actualizar módulo');
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchWaStatus();
    fetchEmailConfig();
    fetchAfipConfig();
    fetchSectionToggles();
  }, []);

  const realtimeTimerRef = useRef<number | null>(null);
  const pendingRefreshRef = useRef(false);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (
        path &&
        !(
          path.startsWith('/api/settings') ||
          path.startsWith('/api/whatsapp') ||
          path.startsWith('/api/notifications') ||
          path.startsWith('/api/email-config') ||
          path.startsWith('/api/afip') ||
          path.startsWith('/api/daily-reports') ||
          path.startsWith('/api/users') ||
          path.startsWith('/api/roles') ||
          path.startsWith('/api/custom-discounts') ||
          path.startsWith('/api/coupons') ||
          path.startsWith('/api/budgets')
        )
      ) {
        return;
      }

      const modalOpen =
        showCompanyModal ||
        showMessageTemplatesModal ||
        showEditTemplateModal ||
        showCustomDiscountModal ||
        showCouponsModal ||
        showDailyReportModal ||
        showNotificationModal ||
        showAfipConfigModal ||
        showDeleteConfirm;

      if (modalOpen) {
        pendingRefreshRef.current = true;
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchUsers();
        fetchWaStatus();
        fetchPaymentMethods();
        fetchApprovedBudgets();
        fetchEmailConfig();
        fetchAfipConfig();
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [
    showCompanyModal,
    showMessageTemplatesModal,
    showEditTemplateModal,
    showCustomDiscountModal,
    showCouponsModal,
    showDailyReportModal,
    showNotificationModal,
    showAfipConfigModal,
    showDeleteConfirm
  ]);

  useEffect(() => {
    const modalOpen =
      showCompanyModal ||
      showMessageTemplatesModal ||
      showEditTemplateModal ||
      showCustomDiscountModal ||
      showCouponsModal ||
      showDailyReportModal ||
      showNotificationModal ||
      showAfipConfigModal ||
      showDeleteConfirm;

    if (modalOpen) return;
    if (!pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    fetchUsers();
    fetchWaStatus();
    fetchPaymentMethods();
    fetchApprovedBudgets();
    fetchEmailConfig();
    fetchAfipConfig();
  }, [
    showCompanyModal,
    showMessageTemplatesModal,
    showEditTemplateModal,
    showCustomDiscountModal,
    showCouponsModal,
    showDailyReportModal,
    showNotificationModal,
    showAfipConfigModal,
    showDeleteConfirm
  ]);

  // Company handlers
  const resetCompanyLogoState = () => {
    if (companyLogoPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(companyLogoPreview);
    }
    setCompanyLogoFile(null);
    setCompanyLogoPreview(null);
  };

  const onCompanyLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (companyLogoPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(companyLogoPreview);
    }
    setCompanyLogoFile(file || null);
    if (file) {
      const url = URL.createObjectURL(file);
      setCompanyLogoPreview(url);
    } else {
      setCompanyLogoPreview(null);
    }
  };

  const fetchCompanyAreas = useCallback(async () => {
    try {
      setCompanyAreasLoading(true);
      const res = await axios.get('/api/settings/areas');
      setCompanyAreas(res.data?.areas || []);
    } catch (error: any) {
      console.error('Error fetching areas', error);
      toast.error(error.response?.data?.error || 'No se pudieron cargar las reas');
    } finally {
      setCompanyAreasLoading(false);
    }
  }, []);

  const createCompanyArea = async () => {
    if (!companyNewArea.trim()) {
      toast.error('Ingresa un nombre de rea');
      return;
    }
    try {
      setCompanyAreaSaving(true);
      await axios.post('/api/settings/areas', { name: companyNewArea.trim() });
      toast.success('rea creada');
      setCompanyNewArea('');
      fetchCompanyAreas();
    } catch (error: any) {
      console.error('Error creating area', error);
      toast.error(error.response?.data?.error || 'No se pudo crear el rea');
    } finally {
      setCompanyAreaSaving(false);
    }
  };

  const deleteCompanyArea = async (id: string) => {
    if (!await confirmDialog('Eliminar esta rea?')) return;
    try {
      setCompanyAreaDeletingId(id);
      await axios.delete(`/api/settings/areas/${id}`);
      toast.success('rea eliminada');
      fetchCompanyAreas();
    } catch (error: any) {
      console.error('Error deleting area', error);
      toast.error(error.response?.data?.error || 'No se pudo eliminar el rea');
    } finally {
      setCompanyAreaDeletingId(null);
    }
  };

  const fetchSupplyCategories = useCallback(async () => {
    try {
      setSupplyCategoriesLoading(true);
      const res = await axios.get('/api/settings/supply-categories');
      setSupplyCategories(res.data?.categories || []);
    } catch (error: any) {
      console.error('Error fetching supply categories', error);
      toast.error(error.response?.data?.error || 'No se pudieron cargar las categoras');
    } finally {
      setSupplyCategoriesLoading(false);
    }
  }, []);

  const createSupplyCategory = async () => {
    if (!newSupplyCategory.trim()) {
      toast.error('Ingresa un nombre de categora');
      return;
    }
    try {
      setSupplyCategorySaving(true);
      await axios.post('/api/settings/supply-categories', { name: newSupplyCategory.trim() });
      toast.success('Categora creada');
      setNewSupplyCategory('');
      fetchSupplyCategories();
    } catch (error: any) {
      console.error('Error creating supply category', error);
      toast.error(error.response?.data?.error || 'No se pudo crear la categora');
    } finally {
      setSupplyCategorySaving(false);
    }
  };

  const deleteSupplyCategory = async (id: string) => {
    if (!await confirmDialog('Eliminar esta categora?')) return;
    try {
      setSupplyCategoryDeletingId(id);
      await axios.delete(`/api/settings/supply-categories/${id}`);
      toast.success('Categora eliminada');
      fetchSupplyCategories();
    } catch (error: any) {
      console.error('Error deleting supply category', error);
      toast.error(error.response?.data?.error || 'No se pudo eliminar la categora');
    } finally {
      setSupplyCategoryDeletingId(null);
    }
  };

  const fetchObservationTypes = useCallback(async () => {
    try {
      setObservationTypesLoading(true);
      const res = await axios.get('/api/settings/observation-types');
      setObservationTypes(res.data?.observationTypes || []);
    } catch (error: any) {
      console.error('Error fetching observation types', error);
      toast.error(error.response?.data?.error || 'No se pudieron cargar los tipos de observacion');
    } finally {
      setObservationTypesLoading(false);
    }
  }, []);

  const createObservationType = async () => {
    if (!newObservationType.trim()) {
      toast.error('Ingresa un nombre para el tipo de observacion');
      return;
    }
    try {
      setObservationTypeSaving(true);
      await axios.post('/api/settings/observation-types', { name: newObservationType.trim() });
      toast.success('Tipo de observacion creado');
      setNewObservationType('');
      fetchObservationTypes();
    } catch (error: any) {
      console.error('Error creating observation type', error);
      toast.error(error.response?.data?.error || 'No se pudo crear el tipo de observacion');
    } finally {
      setObservationTypeSaving(false);
    }
  };

  const deleteObservationType = async (id: string) => {
    if (!await confirmDialog('Eliminar este tipo de observacion?')) return;
    try {
      setObservationTypeDeletingId(id);
      await axios.delete(`/api/settings/observation-types/${id}`);
      toast.success('Tipo de observacion eliminado');
      fetchObservationTypes();
    } catch (error: any) {
      console.error('Error deleting observation type', error);
      toast.error(error.response?.data?.error || 'No se pudo eliminar el tipo de observacion');
    } finally {
      setObservationTypeDeletingId(null);
    }
  };

  const addCacheBust = (url: string): string => {
    if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
      return url;
    }
    const [base, query = ''] = url.split('?');
    const filteredQuery = query
      .split('&')
      .filter((param) => param && !param.startsWith('ts='))
      .join('&');
    const newQuery = filteredQuery ? `${filteredQuery}&ts=${Date.now()}` : `ts=${Date.now()}`;
    return `${base}?${newQuery}`;
  };

  const resolveLogoPreview = (logoValue?: string | null): string | null => {
    if (!logoValue) return null;
    const trimmedValue = logoValue.trim();
    if (!trimmedValue) return null;
    if (trimmedValue.startsWith('blob:') || /^data:image|^https?:\/\//i.test(trimmedValue)) {
      return trimmedValue;
    }

    const normalized = trimmedValue.replace(/\\/g, '/');
    const uploadsIndex = normalized.indexOf('/uploads/');
    if (uploadsIndex >= 0) {
      const resolved = getImageUrl(normalized.slice(uploadsIndex));
      return resolved ? addCacheBust(resolved) : null;
    }

    const backendOrigin = getBackendOrigin();
    const withLeadingSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;

    if (withLeadingSlash.startsWith('/api/')) {
      return addCacheBust(`${backendOrigin}${withLeadingSlash}`);
    }

    const sanitizedValue = normalized
      .split('?')[0]
      .replace(/^\/+/, '')
      .replace(/^api\/settings\/company\/logo\//i, '')
      .replace(/^uploads\/company\//i, '')
      .trim();

    if (!sanitizedValue) {
      return null;
    }

    return addCacheBust(
      `${backendOrigin}/api/settings/company/logo/${encodeURIComponent(sanitizedValue)}`
    );
  };

  const onSubmitCompany = async (data: CompanyInfoForm) => {
    try {
      // Save company info using the new endpoint
      await axios.post('/api/settings/company', data);

      // Upload logo if provided
      if (companyLogoFile) {
        const formData = new FormData();
        formData.append('logo', companyLogoFile);
        await axios.post('/api/settings/company/logo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        // Notificar al layout para que refresque el logo
        window.dispatchEvent(new CustomEvent('company-logo-updated'));
      }

      // Limpiar el archivo temporal
      setCompanyLogoFile(null);

      // Recargar los datos de la empresa para mostrar el logo guardado
      const res = await axios.get('/api/settings/company');
      const companyData = res.data || {};

      // Revocar el blob URL anterior si existe
      if (companyLogoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(companyLogoPreview);
      }

      // Actualizar la preview con la imagen del servidor
      const previewUrl = resolveLogoPreview(companyData.logo);
      setCompanyLogoPreview(previewUrl);

      toast.success('Información de la empresa guardada');
      setShowCompanyModal(false);
    } catch (error: any) {
      console.error('Error guardando informacin de empresa:', error);
      toast.error(error.response?.data?.error || 'Error guardando informacin de la empresa');
    }
  };

  // Cargar configuración de empresa cuando se abre el modal
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await axios.get('/api/settings/company');
        const companyData = res.data || {};
        setCompanyValue('name', companyData.name || '');
        setCompanyValue('address', companyData.address || '');
        setCompanyValue('phone', companyData.phone || '');
        setCompanyValue('whatsapp', companyData.whatsapp || '');
        setCompanyValue('email', companyData.email || '');
        setCompanyValue('instagram', companyData.instagram || '');
        setCompanyValue('cuit', companyData.cuit || '');
        setCompanyValue('iibb', companyData.iibb || '');
        setCompanyValue('taxStatus', companyData.taxStatus || '');
        setCompanyValue('activityStartDate', companyData.activityStartDate || '');
        setCompanyValue('weekdayHours', companyData.weekdayHours || '');
        setCompanyValue('saturdayHours', companyData.saturdayHours || '');
        const previewUrl = resolveLogoPreview(companyData.logo);
        setCompanyLogoPreview(previewUrl);
      } catch (e) {
        // Ignorar si no hay aún configuración
      }
    };
    if (showCompanyModal) {
      fetchCompany();
      fetchCompanyAreas();
      fetchSupplyCategories();
      fetchObservationTypes();
    }
  }, [showCompanyModal, setCompanyValue, fetchCompanyAreas, fetchSupplyCategories, fetchObservationTypes]);

      // Cargar datos al montar el componente
    useEffect(() => {
      fetchApprovedBudgets();
    }, []);

  // Cargar plantillas de mensajes cuando se abre el modal
  useEffect(() => {
    if (showMessageTemplatesModal) {
      fetchMessageTemplates();
      fetchEmailTemplates();
    }
  }, [showMessageTemplatesModal]);

  // Cargar configuración de reportes diarios cuando se abre el modal
  useEffect(() => {
    if (showDailyReportModal) {
      fetchDailyReportConfig();
    }
  }, [showDailyReportModal]);

  // Cargar notificaciones cuando se abre el modal
  useEffect(() => {
    if (showNotificationModal) {
      fetchNotifications();
    }
  }, [showNotificationModal]);

  // Cargar metodos de pago al montar el componente
  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  // Cargar descuentos personalizados cuando se abre el modal
  useEffect(() => {
    if (showCustomDiscountModal) {
      fetchCustomDiscounts();
    }
  }, [showCustomDiscountModal]);

  const fetchWaStatus = async () => {
    try {
      setWaLoading(true);
      const res = await axios.get('/api/whatsapp/status');
      const status = res.data.status;
      // Mapear estados de Kapso a estados del frontend
      if (status === 'not_configured') {
        setWaStatus('disconnected');
      } else {
        setWaStatus(status || 'unknown');
      }

      // Obtener diagnstico de Kapso para saber si est configurado
      try {
        const diagRes = await axios.get('/api/whatsapp/diagnostic');
        if (diagRes.data.diagnostic) {
          setKapsoConfig({
            hasApiKey: diagRes.data.diagnostic.hasApiKey,
            hasPhoneNumberId: diagRes.data.diagnostic.hasPhoneNumberId,
            phoneNumberId: diagRes.data.diagnostic.phoneNumberId
          });
        }
      } catch {
        // Ignorar errores de diagnstico
      }
    } catch {
      setWaStatus('unknown');
    } finally {
      setWaLoading(false);
    }
  };

  // Funciones para Notificaciones
  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/api/notifications/config');
      console.log('Notifications data received:', response.data);
      
      // Ensure all notifications have the expected structure
      const normalizedNotifications = response.data.map((notification: any) => ({
        id: notification.id,
        type: notification.type || 'system',
        name: notification.name || 'Sin nombre',
        description: notification.description || 'Sin descripcin',
        enabled: notification.enabled !== undefined ? notification.enabled : true,
        channels: Array.isArray(notification.channels) ? notification.channels : ['system'],
        schedule: notification.schedule || 'immediate',
        recipients: Array.isArray(notification.recipients) ? notification.recipients : ['admin'],
        conditions: notification.conditions || {},
        created_at: notification.created_at || new Date().toISOString(),
        updated_at: notification.updated_at || new Date().toISOString()
      }));
      
      setNotifications(normalizedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Error al cargar las notificaciones');
    }
  };

  const createNotification = async (data: NotificationForm) => {
    try {
      await axios.post('/api/notifications/config', data);
      toast.success('Notificacin creada exitosamente');
      setShowNotificationModal(false);
      resetNotification();
      fetchNotifications();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al crear la notificacion');
    }
  };

  const updateNotification = async (data: NotificationForm) => {
    if (!selectedNotification) return;
    
    try {
      await axios.put(`/api/notifications/config/${selectedNotification.id}`, data);
      toast.success('Notificacin actualizada exitosamente');
      setShowNotificationModal(false);
      resetNotification();
      setSelectedNotification(null);
      fetchNotifications();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar la notificacion');
    }
  };

  const toggleNotification = async (id: number, enabled: boolean) => {
    try {
      await axios.patch(`/api/notifications/config/${id}`, { enabled });
      toast.success(`Notificacin ${enabled ? 'activada' : 'desactivada'} exitosamente`);
      fetchNotifications();
    } catch (error: any) {
      toast.error('Error al cambiar el estado de la notificacion');
    }
  };

  const deleteNotification = async (id: number) => {
    setNotificationToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteNotification = async () => {
    if (!notificationToDelete) return;
    
    try {
      await axios.post(`/api/notifications/config/${notificationToDelete}`, { _method: 'DELETE' });
      toast.success('Notificacin eliminada exitosamente');
      fetchNotifications();
    } catch (error: any) {
      toast.error('Error al eliminar la notificacion');
    } finally {
      setShowDeleteConfirm(false);
      setNotificationToDelete(null);
    }
  };

  const handleEditNotification = (notification: NotificationConfig) => {
    setSelectedNotification(notification);
    resetNotification({
      type: notification.type || 'system',
      name: notification.name || '',
      description: notification.description || '',
      enabled: notification.enabled !== undefined ? notification.enabled : true,
      channels: Array.isArray(notification.channels) ? notification.channels : ['system'],
      schedule: notification.schedule || 'immediate',
      recipients: Array.isArray(notification.recipients) ? notification.recipients : ['admin'],
      conditions: notification.conditions || {}
    });
  };

  const handleCreateNotification = () => {
    setSelectedNotification(null);
    resetNotification({
      type: 'email',
      name: '',
      description: '',
      enabled: true,
      channels: ['email'],
      schedule: 'immediate',
      recipients: ['admin'],
      conditions: {}
    });
  };

  const createStockAlertNotification = async () => {
    try {
      const stockAlertData = {
        type: 'system',
        name: 'Alerta de Stock Bajo',
        description: 'Notifica cuando los productos tengan stock bajo o sin stock',
        enabled: true,
        channels: ['email', 'whatsapp'],
        schedule: 'daily',
        recipients: ['admin', 'manager'],
        conditions: {
          stockThreshold: 10,
          checkFrequency: 'daily',
          includeOutOfStock: true
        }
      };

      await axios.post('/api/notifications/config', stockAlertData);
      toast.success('Alerta de stock creada exitosamente');
      fetchNotifications();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al crear la alerta de stock');
    }
  };

  // Funciones para Mtodos de Pago
  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get('/api/settings/payment-methods');
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.paymentMethods)
          ? response.data.paymentMethods
          : Array.isArray(response.data?.methods)
            ? response.data.methods
            : [];
      setPaymentMethods(data);
    } catch (error: any) {
      console.error('Error cargando metodos de pago:', error);
    }
  };

  // Funciones para Descuentos Personalizados
  const fetchCustomDiscounts = async () => {
    try {
      setLoadingCustomDiscounts(true);
      const response = await axios.get('/api/custom-discounts');
      setCustomDiscounts(response.data);
    } catch (error: any) {
      console.error('Error cargando descuentos personalizados:', error);
      toast.error('Error al cargar los descuentos personalizados');
    } finally {
      setLoadingCustomDiscounts(false);
    }
  };

  const createCustomDiscount = async (data: CustomDiscountForm) => {
    try {
      await axios.post('/api/custom-discounts', data);
      toast.success('Descuento personalizado creado exitosamente');
      setShowCustomDiscountModal(false);
      resetCustomDiscount();
      fetchCustomDiscounts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al crear el descuento personalizado');
    }
  };

  const updateCustomDiscount = async (data: CustomDiscountForm) => {
    if (!editingCustomDiscount) return;
    
    try {
      await axios.put(`/api/custom-discounts/${editingCustomDiscount.id}`, data);
      toast.success('Descuento personalizado actualizado exitosamente');
      setShowCustomDiscountModal(false);
      resetCustomDiscount();
      setEditingCustomDiscount(null);
      fetchCustomDiscounts();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar el descuento personalizado');
    }
  };

  const deleteCustomDiscount = async (id: string) => {
    try {
      await axios.delete(`/api/custom-discounts/${id}`);
      toast.success('Descuento personalizado eliminado exitosamente');
      fetchCustomDiscounts();
    } catch (error: any) {
      toast.error('Error al eliminar el descuento personalizado');
    }
  };

  const toggleCustomDiscount = async (id: string, enabled: boolean) => {
    try {
      await axios.patch(`/api/custom-discounts/${id}`, { enabled });
      toast.success(`Descuento personalizado ${enabled ? 'activado' : 'desactivado'} exitosamente`);
      fetchCustomDiscounts();
    } catch (error: any) {
      toast.error('Error al cambiar el estado del descuento personalizado');
    }
  };

  const handleEditCustomDiscount = (discount: CustomDiscount) => {
    setEditingCustomDiscount(discount);
    setCustomDiscountValue('name', discount.name);
    setCustomDiscountValue('description', discount.description || '');
    setCustomDiscountValue('discount_type', discount.discount_type);
    setCustomDiscountValue('discount_value', discount.discount_value);
    setCustomDiscountValue('min_amount', discount.min_amount);
    setCustomDiscountValue('max_discount', discount.max_discount || 0);
    setCustomDiscountValue('applies_to', discount.applies_to);
    setCustomDiscountValue('target_id', discount.target_id || '');
    setCustomDiscountValue('target_name', discount.target_name || '');
    setCustomDiscountValue('start_date', discount.start_date);
    setCustomDiscountValue('end_date', discount.end_date);
    setCustomDiscountValue('active_days', discount.active_days);
    setCustomDiscountValue('active_hours', discount.active_hours);
    setCustomDiscountValue('max_uses', discount.max_uses || 100);
    setCustomDiscountValue('priority', discount.priority);
  };

  const handleCreateCustomDiscount = () => {
    setEditingCustomDiscount(null);
    resetCustomDiscount();
  };

  // Funciones para Cupones
  const fetchCoupons = async () => {
    try {
      setLoadingCoupons(true);
      const response = await axios.get('/api/coupons');
      setCoupons(response.data.coupons);
    } catch (error: any) {
      console.error('Error cargando cupones:', error);
      toast.error('Error al cargar los cupones');
    } finally {
      setLoadingCoupons(false);
    }
  };

  const createCoupon = async (data: CouponForm) => {
    try {
      await axios.post('/api/coupons', data);
      toast.success('Cupón creado exitosamente');
      setShowCouponsModal(false);
      resetCoupon();
      fetchCoupons();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al crear el cupón');
    }
  };

  const updateCoupon = async (data: CouponForm) => {
    if (!editingCoupon) return;
    
    try {
      await axios.put(`/api/coupons/${editingCoupon.id}`, {
        ...data,
        enabled: true
      });
      toast.success('Cupón actualizado exitosamente');
      setShowCouponsModal(false);
      resetCoupon();
      setEditingCoupon(null);
      fetchCoupons();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar el cupón');
    }
  };

  const deleteCoupon = async (id: string) => {
    try {
      await axios.delete(`/api/coupons/${id}`);
      toast.success('Cupón eliminado exitosamente');
      fetchCoupons();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar el cupón');
    }
  };

  const toggleCoupon = async (id: string, enabled: boolean) => {
    try {
      await axios.put(`/api/coupons/${id}`, { enabled });
      toast.success(`Cupón ${enabled ? 'activado' : 'desactivado'} exitosamente`);
      fetchCoupons();
    } catch (error: any) {
      toast.error('Error al cambiar el estado del cupón');
    }
  };

  const handleEditCoupon = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setCouponValue('code', coupon.code);
    setCouponValue('name', coupon.name);
    setCouponValue('description', coupon.description || '');
    setCouponValue('discount_type', coupon.discount_type);
    setCouponValue('discount_value', coupon.discount_value);
    setCouponValue('min_amount', coupon.min_amount);
    setCouponValue('max_discount', coupon.max_discount || 0);
    setCouponValue('applies_to', coupon.applies_to);
    setCouponValue('target_id', coupon.target_id || '');
    setCouponValue('target_name', coupon.target_name || '');
    setCouponValue('start_date', coupon.start_date);
    setCouponValue('end_date', coupon.end_date);
    setCouponValue('max_uses', coupon.max_uses || 100);
    setCouponValue('max_uses_per_client', coupon.max_uses_per_client || 1);
  };

  const handleCreateCoupon = () => {
    setEditingCoupon(null);
    resetCoupon();
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  // Funcin para cargar presupuestos aprobados
  const fetchApprovedBudgets = async () => {
    try {
      const response = await axios.get('/api/service-reports/approved-budgets');
      setApprovedBudgets(response.data.budgets);
    } catch (error) {
      console.error('Error al cargar presupuestos aprobados:', error);
    }
  };



  // Funciones para manejar plantillas de mensajes
  const fetchMessageTemplates = async () => {
    try {
      console.log('Iniciando fetchMessageTemplates');
      setLoadingTemplates(true);
      const response = await axios.get('/api/settings/messages');
      console.log('Respuesta del servidor:', response.data);
      setMessageTemplates(response.data.templates || {});
    } catch (error: any) {
      console.error('Error fetching message templates:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Error al cargar plantillas de mensajes');
      setMessageTemplates({}); // Fallback a objeto vaco
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await axios.get('/api/settings/email-templates');
      setEmailTemplates(response.data.templates);
    } catch (error: any) {
      console.error('Error fetching email templates:', error);
      // Inicializar con plantillas por defecto si no existen
      setEmailTemplates({
        budget_sent: {
          name: 'Presupuesto Enviado',
          description: 'Email enviado cuando se comparte un presupuesto',
          subject: 'Presupuesto {budgetNumber} - ROCAM',
          body: 'Hola {clientName},\n\nAdjunto el presupuesto {budgetNumber} por un valor de {totalAmount}.\n\n{description}\n\nPrecios por medio de pago:\n{paymentOptions}\n\nEste presupuesto es válido por 30 días desde su emisión.',
          variables: ['clientName', 'budgetNumber', 'totalAmount', 'description', 'paymentOptions']
        },
        budget_approved: {
          name: 'Presupuesto Aprobado',
          description: 'Email enviado cuando se aprueba un presupuesto',
          subject: 'Presupuesto APROBADO {budgetNumber} - ROCAM',
          body: 'Hola {clientName},\n\n¡Excelentes noticias! Tu presupuesto {budgetNumber} por {totalAmount} ha sido APROBADO.\n\n{observations}\n\nAdjunto encontrarás el presupuesto aprobado en PDF.\n\nNos pondremos en contacto pronto para coordinar el inicio del trabajo.\n\n¡Gracias por confiar en nosotros!',
          variables: ['clientName', 'budgetNumber', 'totalAmount', 'observations']
        },
        budget_rejected: {
          name: 'Presupuesto Rechazado',
          description: 'Email enviado cuando se rechaza un presupuesto',
          subject: 'Presupuesto RECHAZADO {budgetNumber} - ROCAM',
          body: 'Hola {clientName},\n\nLamentamos informarte que tu presupuesto {budgetNumber} por {totalAmount} ha sido RECHAZADO.\n\n{reason}\n\nSi tenés alguna consulta o querés que revisemos el presupuesto nuevamente, no dudes en contactarnos.\n\nEstamos aquí para ayudarte a encontrar la mejor solución para tu vehículo.',
          variables: ['clientName', 'budgetNumber', 'totalAmount', 'reason']
        },
        remito_sent: {
          name: 'Remito Enviado',
          description: 'Email enviado cuando se comparte un remito',
          subject: 'Remito {remitoNumber} - ROCAM',
          body: 'Hola {clientName},\n\nAdjunto el remito {remitoNumber} por un valor de {totalAmount}.\n\n{observations}',
          variables: ['clientName', 'remitoNumber', 'totalAmount', 'observations']
        }
      });
    }
  };

  const updateMessageTemplates = async (templates: MessageTemplates) => {
    try {
      setLoadingTemplates(true);
      await axios.put('/api/settings/messages', { templates });
      setMessageTemplates(templates);
      toast.success('Plantillas de mensajes actualizadas correctamente');
      setShowMessageTemplatesModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar plantillas');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Funcin para insertar variables en el textarea activo
  const insertVariable = (variable: string) => {
    if (activeTextareaRef) {
      const textarea = activeTextareaRef;
      const start = textarea.selectionStart ?? 0;
      const end = textarea.selectionEnd ?? 0;
      const value = textarea.value;
      
      // Insertar la variable en la posicin del cursor
      const newValue = value.substring(0, start) + variable + value.substring(end);
      
      // Actualizar el valor del textarea
      textarea.value = newValue;
      
      // Disparar el evento change manualmente
      const event = new Event('input', { bubbles: true });
      textarea.dispatchEvent(event);
      
      // Mover el cursor despus de la variable insertada
      setTimeout(() => {
        const newPosition = start + variable.length;
        textarea.selectionStart = newPosition;
        textarea.selectionEnd = newPosition;
        textarea.focus();
      }, 0);
    }
  };

  const updateEmailTemplates = async (templates: {[key: string]: {
    name: string;
    description: string;
    subject: string;
    body: string;
    variables: string[];
    enabled?: boolean;
  }}) => {
    try {
      setLoadingTemplates(true);
      await axios.put('/api/settings/email-templates', { templates });
      setEmailTemplates(templates);
      toast.success('Plantillas de email actualizadas correctamente');
      setShowMessageTemplatesModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al actualizar plantillas de email');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Funciones para reportes diarios
  const fetchDailyReportConfig = async () => {
    try {
      const response = await axios.get('/api/daily-reports/config');
      if (response.data.success) {
        setDailyReportConfig(response.data.config);
      }
    } catch (error: any) {
      console.error('Error obteniendo configuración de reportes diarios:', error);
      toast.error('Error al cargar la configuración de reportes diarios');
    }
  };

  const updateDailyReportConfig = async () => {
    try {
      setDailyReportLoading(true);
      const response = await axios.put('/api/daily-reports/config', dailyReportConfig);
      
      if (response.data.success) {
        toast.success('Configuración de reportes diarios actualizada correctamente');
        setShowDailyReportModal(false);
        fetchDailyReportConfig(); // Recargar configuración
      } else {
        throw new Error(response.data.error || 'Error al actualizar configuración');
      }
    } catch (error: any) {
      console.error('Error actualizando configuración de reportes diarios:', error);
      toast.error(error.response?.data?.error || 'Error al actualizar la configuración de reportes diarios');
    } finally {
      setDailyReportLoading(false);
    }
  };

  const sendTestDailyReport = async () => {
    try {
      setDailyReportLoading(true);
      const response = await axios.post('/api/daily-reports/send');
      
      if (response.data.success) {
        toast.success('Reporte diario de prueba enviado correctamente');
      } else {
        throw new Error(response.data.error || 'Error al enviar reporte');
      }
    } catch (error: any) {
      console.error('Error enviando reporte de prueba:', error);
      toast.error(error.response?.data?.error || 'Error al enviar el reporte de prueba');
    } finally {
      setDailyReportLoading(false);
    }
  };

  // Función para cargar configuración de email
  const fetchEmailConfig = async () => {
    try {
      const response = await axios.get('/api/email-config');
      if (response.data && Object.keys(response.data).length > 0) {
        setEmailConfig(response.data);
      }
    } catch (error: any) {
      console.log('No hay configuración de email guardada o error:', error.message);
    }
  };
  const fetchAfipConfig = async () => {
    try {
      const response = await axios.get('/api/afip/config');
      const config = response.data || null;
      setAfipConfig(config);
      if (config) {
        resetAfipConfig({
          cuitEmisor: config.cuitEmisor || '',
          puntoVenta: config.puntoVenta || '',
          wsmtxcaWsdlHomologacion: config.wsmtxcaWsdlHomologacion || '',
          wsmtxcaWsdlProduccion: config.wsmtxcaWsdlProduccion || '',
          environment: config.environment || 'homologacion',
          enabled: !!config.enabled
        });
      } else {
        resetAfipConfig({
          cuitEmisor: '',
          puntoVenta: '',
          wsmtxcaWsdlHomologacion: '',
          wsmtxcaWsdlProduccion: '',
          environment: 'homologacion',
          enabled: false
        });
      }
    } catch (error: any) {
      setAfipConfig(null);
    }
  };
  const onSubmitAfipConfig = async (data: AfipConfigForm) => {
    try {
      if (data.enabled && (!data.cuitEmisor?.trim() || !data.puntoVenta?.trim())) {
        toast.error('Completa CUIT emisor y punto de venta.');
        return;
      }

      setAfipConfigLoading(true);

      let certContent: string | undefined;
      let keyContent: string | undefined;

      if (data.cert && data.cert.length > 0) {
        certContent = await data.cert[0].text();
      }

      if (data.key && data.key.length > 0) {
        keyContent = await data.key[0].text();
      }

      const payload: Record<string, any> = {
        cuitEmisor: data.cuitEmisor?.trim() || null,
        puntoVenta: data.puntoVenta?.trim() || null,
        wsmtxcaWsdlHomologacion: data.wsmtxcaWsdlHomologacion?.trim() || null,
        wsmtxcaWsdlProduccion: data.wsmtxcaWsdlProduccion?.trim() || null,
        environment: data.environment || 'homologacion',
        preferredWs: data.preferredWs || 'wsfe',
        enabled: data.enabled
      };

      if (certContent !== undefined) {
        payload.cert = certContent;
      }
      if (keyContent !== undefined) {
        payload.key = keyContent;
      }

      await axios.post('/api/afip/config', payload);

      toast.success('Configuración de AFIP guardada exitosamente');
      setShowAfipConfigModal(false);
      resetAfipConfig();
      await fetchAfipConfig(); // Cargar configuración de AFIP
    } catch (error: any) {
      console.error('Error guardando configuración de AFIP:', error);
      toast.error(error.response?.data?.error || 'Error al guardar la configuración');
    } finally {
      setAfipConfigLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Configuración del Sistema</CardTitle>
          <CardDescription>
            Gestiona todas las configuraciones y módulos del sistema
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Sistema - Configuraciones en Lista */}
      <Card>
        <CardHeader>
          <CardTitle>Configuraciones del Sistema</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Company Information */}
            <div
              className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
              onClick={() => navigate('/settings/company')}
            >
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <BuildingOfficeIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">Información de la empresa</h3>
                  <p className="text-sm text-muted-foreground">Nombre, contacto y logo</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/settings/company');
                }}
              >
                Abrir
              </Button>
            </div>
            {/* WhatsApp Module - Kapso */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">WhatsApp Business API</h3>
                  <p className="text-sm text-muted-foreground">Envío de mensajes vía Kapso (Cloud API)</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={kapsoConfig?.hasApiKey && kapsoConfig?.hasPhoneNumberId ? 'default' : 'destructive'}>
                      {kapsoConfig?.hasApiKey && kapsoConfig?.hasPhoneNumberId ? 'Configurado' : 'No configurado'}
                    </Badge>
                    {waStatus === 'connected' && (
                      <Badge variant="default">Conectado</Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => navigate('/settings/whatsapp')} variant="outline" size="sm">
                  Configurar
                </Button>
                {kapsoConfig?.hasApiKey && kapsoConfig?.hasPhoneNumberId && (
                  <Button
                    onClick={() => fetchWaStatus()}
                    disabled={waLoading}
                    variant="default"
                    size="sm"
                  >
                    Verificar
                  </Button>
                )}
              </div>
            </div>

            {/* Email Configuration */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <EnvelopeIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">Configuración de Email</h3>
                  <p className="text-sm text-muted-foreground">Gmail, Ferozo o SMTP personalizado</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={emailConfig?.enabled ? 'default' : 'secondary'}>
                      {emailConfig?.enabled ? 'Configurado' : 'No configurado'}
                    </Badge>
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/email-config')}>
                Configurar
              </Button>
            </div>

            {/* Facturación AFIP Configuration */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <BuildingOfficeIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">Facturación AFIP (WSMTXCA)</h3>
                  <p className="text-sm text-muted-foreground">CUIT emisor, punto de venta y certificados</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={afipConfig?.enabled && afipConfig?.hasCert && afipConfig?.hasKey ? 'default' : 'destructive'}>
                      {afipConfig?.enabled && afipConfig?.hasCert && afipConfig?.hasKey ? 'Configurado' : 'No configurado'}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAfipConfigModal(true);
                    setAfipConfigValue('cuitEmisor', afipConfig?.cuitEmisor || '');
                    setAfipConfigValue('puntoVenta', afipConfig?.puntoVenta || '');
                    setAfipConfigValue('wsmtxcaWsdlHomologacion', afipConfig?.wsmtxcaWsdlHomologacion || '');
                    setAfipConfigValue('wsmtxcaWsdlProduccion', afipConfig?.wsmtxcaWsdlProduccion || '');
                    setAfipConfigValue('environment', afipConfig?.environment || 'homologacion');
                    setAfipConfigValue('preferredWs', afipConfig?.preferredWs || 'wsfe');
                    setAfipConfigValue('enabled', afipConfig?.enabled || false);
                  }}
                >
                  Configurar
                </Button>
                {afipConfig && (
                  <Button variant="ghost" size="sm" onClick={fetchAfipConfig}>
                    Recargar
                  </Button>
                )}
              </div>
            </div>
            {/* Medios de Pago */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
              onClick={() => navigate('/payment-methods')}>
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <CreditCardIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">Medios de Pago</h3>
                  <p className="text-sm text-muted-foreground">Gestiona métodos de pago, recargos y configuraciones</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">Total: {paymentMethods.length} métodos configurados</Badge>
                  </div>
                </div>
              </div>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/payment-methods');
                  }}
                >
                  Configurar
                </Button>
              </div>
            </div>


            {/* Users Management */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer" onClick={() => navigate('/users')}>
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <UsersIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">Gestion de usuarios</h3>
                  <p className="text-sm text-muted-foreground">Ver y administrar usuarios del sistema</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">Total: {users.length} usuarios</Badge>
                  </div>
                </div>
              </div>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/users');
                  }}
                >
                  Ir a usuarios
                </Button>
              </div>
            </div>
            {/* Roles & Permisos */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer" onClick={() => navigate('/roles')}>
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <ShieldCheckIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">Roles y permisos</h3>
                  <p className="text-sm text-muted-foreground">Configura roles disponibles y asignacion</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">Roles activos: {rolesCount}</Badge>
                  </div>
                </div>
              </div>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/roles');
                  }}
                >
                  Configurar roles
                </Button>
              </div>
            </div>
            {/* Skills / Especialidades */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer" onClick={() => navigate('/skills')}>
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <WrenchScrewdriverIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">Skills</h3>
                  <p className="text-sm text-muted-foreground">Especialidades que se asignan a usuarios y productos</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">Skills: {skillsCount}</Badge>
                  </div>
                </div>
              </div>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/skills');
                  }}
                >
                  Configurar skills
                </Button>
              </div>
            </div>
            {/* Respaldos */}
            <div
              className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
              onClick={() => navigate('/backups')}
            >
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <CloudArrowDownIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">Respaldos</h3>
                  <p className="text-sm text-muted-foreground">Copias de seguridad y restauracion</p>
                </div>
              </div>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/backups');
                  }}
                >
                  Abrir
                </Button>
              </div>
            </div>
            {/* Message Templates */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <MegaphoneIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">Mensajes del Sistema</h3>
                  <p className="text-sm text-muted-foreground">Personaliza mensajes automticos de WhatsApp y Email</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">Turnos, Presupuestos, Notificaciones, Plantillas de Email</Badge>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMessageTemplatesModal(true)}
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Editar
                </Button>
              </div>
            </div>

          {/* Notifications */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-muted p-2">
                <BellIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-medium">Notificaciones</h3>
                <p className="text-sm text-muted-foreground">Configura alertas y notificaciones del sistema</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNotificationModal(true)}
              >
                Configurar
              </Button>
            </div>
          </div>

            {/* Daily Reports */}
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2">
                  <ChartBarIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">Reportes Diarios Automticos</h3>
                  <p className="text-sm text-muted-foreground">Recibe resmenes diarios por email y WhatsApp</p>
                  <div className="flex items-center gap-2 mt-1">
                    {dailyReportConfig.enabled ? (
                      <Badge variant="default">
                        Activo - Lun-Vie: {dailyReportConfig.send_time}, Sb: {dailyReportConfig.saturday_send_time}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Desactivado</Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDailyReportModal(true)}
                >
                  Configurar
                </Button>
              </div>
            </div>

          {/* System Status */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-muted p-2">
                <InformationCircleIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-medium text-muted-foreground">Estados del Sistema</h3>
                <p className="text-sm text-muted-foreground">Monitorea el estado y rendimiento del sistema</p>
              </div>
            </div>
            <Badge variant="secondary">Prximamente</Badge>
          </div>

            {/* General Parameters */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-muted p-2">
                <Cog6ToothIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-medium text-muted-foreground">Parmetros Generales</h3>
                <p className="text-sm text-muted-foreground">Ajusta Configuraciones generales del sistema</p>
              </div>
            </div>
            <Badge variant="secondary">Prximamente</Badge>
          </div>

          </div>
        </CardContent>
      </Card>

      {/* Módulos del Sistema */}
      <Card>
        <CardHeader>
          <CardTitle>Módulos del Sistema</CardTitle>
          <CardDescription>Activa o desactiva secciones del menú según las necesidades de tu negocio</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {sectionToggles.map((toggle) => (
              <div key={toggle.key} className="flex items-center justify-between p-4 rounded-lg border bg-card">
                <div className="flex items-center gap-3">
                  <div className="rounded-md bg-muted p-2">
                    <PowerIcon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-medium">{toggle.label}</h3>
                    <p className="text-sm text-muted-foreground">{toggle.description}</p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!toggle.enabled}
                  onClick={() => handleToggleSection(toggle.key, !toggle.enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${toggle.enabled ? 'bg-primary' : 'bg-input'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-background shadow-lg ring-0 transition-transform ${toggle.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Condiciones de pago escalonadas de cta cte (feature dormida hasta activar) */}
      <PaymentConditionsSettings />

      {/* Escáneres: dataset para reentrenar (Svetovid) */}
      <VisionDatasetSettings />

      {/* Security */}

          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-muted p-2">
                <ShieldCheckIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-medium text-muted-foreground">Seguridad</h3>
                <p className="text-sm text-muted-foreground">Configura polticas de seguridad y acceso</p>
              </div>
            </div>
            <Badge variant="secondary">Prximamente</Badge>
          </div>

      {/* Modal Información empresa */}
      {showCompanyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm px-4 py-6">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl flex flex-col">
            <div className="flex items-center justify-between border-b border-border px-6 py-5">
              <div className="flex items-center gap-3 text-foreground">
                <div className="rounded-xl bg-muted p-2 text-muted-foreground">
                  <BuildingOfficeIcon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-semibold">Información de la empresa</h3>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setShowCompanyModal(false);
                  resetCompanyLogoState();
                }}
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmitCompany(onSubmitCompany)} className="flex-1 overflow-y-auto">
              <div className="space-y-8 bg-card px-6 py-6">
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="space-y-6 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card/60 p-4 shadow-sm">
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-muted-foreground">Datos de contacto</p>
                      <p className="text-sm text-muted-foreground">Se usan en cabeceras, remitos, recordatorios y en la web de contacto.</p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <Label className="text-sm text-muted-foreground">Nombre de la empresa *</Label>
                        <Input
                          {...registerCompany('name', { required: 'Nombre requerido' })}
                          placeholder="Mi empresa SRL"
                        />
                        {companyErrors?.name && (
                          <p className="mt-1 text-xs text-destructive">{companyErrors.name.message}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Telfono</Label>
                        <Input {...registerCompany('phone')} placeholder="3416858698" />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">WhatsApp</Label>
                        <Input {...registerCompany('whatsapp')} placeholder="+54 9 ..." />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Email de contacto web</Label>
                        <Input type="email" {...registerCompany('email')} placeholder="hola@miempresa.com" />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Instagram</Label>
                        <Input {...registerCompany('instagram')} placeholder="@miempresa" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-sm text-muted-foreground">Direccin</Label>
                        <Input {...registerCompany('address')} placeholder="Calle 123, Rosario" />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">CUIT</Label>
                        <Input {...registerCompany('cuit')} placeholder="30-71664233-6" />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Ingresos Brutos</Label>
                        <Input {...registerCompany('iibb')} placeholder="091-728559-8" />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Condicion frente al IVA</Label>
                        <Input {...registerCompany('taxStatus')} placeholder="IVA Responsable Inscripto" />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Fecha Inicio de Actividades</Label>
                        <Input {...registerCompany('activityStartDate')} placeholder="29/10/2019" />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Horario Lun a Vie</Label>
                        <Input {...registerCompany('weekdayHours')} placeholder="8:00 a 18:00" />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Horario Sbado</Label>
                        <Input {...registerCompany('saturdayHours')} placeholder="9:00 a 13:00" />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border bg-card/60 p-4 shadow-sm">
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-muted-foreground">reas de la empresa</p>
                      <p className="text-sm text-muted-foreground">Usadas en usuarios y pedidos internos.</p>
                    </div>

                    <div className="flex flex-col gap-3 md:flex-row md:items-center">
                      <Input
                        placeholder="Nombre del rea"
                        value={companyNewArea}
                        onChange={(e) => setCompanyNewArea(e.target.value)}
                        disabled={companyAreaSaving}
                        className="md:w-64"
                      />
                      <Button onClick={createCompanyArea} disabled={companyAreaSaving}>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        {companyAreaSaving ? 'Guardando...' : 'Crear rea'}
                      </Button>
                    </div>

                    <div className="mt-3 space-y-2">
                      {companyAreasLoading ? (
                        <p className="text-sm text-muted-foreground">Cargando reas...</p>
                      ) : companyAreas.length ? (
                        companyAreas.map((area) => (
                          <div
                            key={area.id}
                            className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2"
                          >
                            <span className="text-sm">{area.name}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteCompanyArea(area.id)}
                              disabled={companyAreaDeletingId === area.id}
                            >
                              <TrashIcon className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">No hay reas configuradas.</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-border bg-card/60 p-4 shadow-sm">
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-muted-foreground">Categoras de insumos</p>
                      <p className="text-sm text-muted-foreground">Para organizar y filtrar el inventario de insumos.</p>
                    </div>

                    <div className="flex flex-col gap-3 md:flex-row md:items-center">
                      <Input
                        placeholder="Nombre de la categora"
                        value={newSupplyCategory}
                        onChange={(e) => setNewSupplyCategory(e.target.value)}
                        disabled={supplyCategorySaving}
                        className="md:w-64"
                      />
                      <Button onClick={createSupplyCategory} disabled={supplyCategorySaving}>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        {supplyCategorySaving ? 'Guardando...' : 'Crear categora'}
                      </Button>
                    </div>

                    <div className="mt-3 space-y-2">
                      {supplyCategoriesLoading ? (
                        <p className="text-sm text-muted-foreground">Cargando categoras...</p>
                      ) : supplyCategories.length ? (
                        supplyCategories.map((category) => (
                          <div
                            key={category.id}
                            className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2"
                          >
                            <span className="text-sm">{category.name}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteSupplyCategory(category.id)}
                              disabled={supplyCategoryDeletingId === category.id}
                            >
                              <TrashIcon className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">No hay categoras configuradas.</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-border bg-card/60 p-4 shadow-sm">
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-muted-foreground">Tipos de observaciones vehiculares</p>
                      <p className="text-sm text-muted-foreground">Los empleados usan estos tipos al reportar problemas detectados en vehiculos.</p>
                    </div>

                    <div className="flex flex-col gap-3 md:flex-row md:items-center">
                      <Input
                        placeholder="Nombre del tipo de observacion"
                        value={newObservationType}
                        onChange={(e) => setNewObservationType(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') createObservationType(); }}
                        disabled={observationTypeSaving}
                        className="md:w-64"
                      />
                      <Button onClick={createObservationType} disabled={observationTypeSaving}>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        {observationTypeSaving ? 'Guardando...' : 'Crear tipo'}
                      </Button>
                    </div>

                    <div className="mt-3 space-y-2">
                      {observationTypesLoading ? (
                        <p className="text-sm text-muted-foreground">Cargando tipos...</p>
                      ) : observationTypes.length ? (
                        observationTypes.map((type) => (
                          <div
                            key={type.id}
                            className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2"
                          >
                            <span className="text-sm">{type.name}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteObservationType(type.id)}
                              disabled={observationTypeDeletingId === type.id}
                            >
                              <TrashIcon className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">No hay tipos de observacion configurados.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4 rounded-xl border border-dashed border-border bg-muted/30 p-4">
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground">Logo visible en PDFs</p>
                    <p className="text-xs text-muted-foreground">PNG o JPG, preferentemente fondo transparente.</p>
                  </div>
                  <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-card/70 p-4 text-center">
                    {companyLogoPreview ? (
                      <img
                        src={companyLogoPreview}
                        alt="Vista previa logo"
                        className="h-24 max-w-full object-contain"
                      />
                    ) : (
                      <div className="flex h-24 w-32 items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground">
                        Sin logo
                      </div>
                    )}
                    <div className="w-full space-y-2">
                      <input
                        id="company-logo"
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={onCompanyLogoChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="company-logo"
                        className="inline-flex w-full cursor-pointer items-center justify-center rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground shadow-sm transition hover:bg-accent hover:text-accent-foreground"
                      >
                        Seleccionar archivo
                      </label>
                      {companyLogoPreview && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full text-sm text-destructive hover:text-destructive"
                          onClick={resetCompanyLogoState}
                        >
                          Quitar logo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 border-t border-border pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCompanyModal(false);
                    resetCompanyLogoState();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90"
                >
                  Guardar cambios
                </Button>
              </div>
              </div>
            </form>
          </div>
        </div>
      )}      {/* Modal de Reportes Diarios */}
      {showDailyReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-border">
            {/* Header */}
            <div className="px-6 py-4 border-b border-border text-foreground">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">Reportes Diarios Automticos</h3>
                  <p className="text-sm text-muted-foreground mt-1">Configura el envo automtico de resmenes diarios</p>
                </div>
                <button
                  onClick={() => setShowDailyReportModal(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="space-y-6">
                {/* Estado y configuración básica */}
                <div className="p-4 rounded-lg border border-border bg-muted/40">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-foreground">Estado del servicio</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {dailyReportConfig.enabled 
                          ? 'Los reportes se envan automticamente' 
                          : 'Los reportes estn desactivados'
                        }
                      </p>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dailyReportConfig.enabled}
                        onChange={(e) => setDailyReportConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                        className="form-checkbox h-5 w-5 text-black dark:text-white"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Activar reportes</span>
                    </label>
                  </div>
                </div>

                {/* Configuración de destinatarios */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email destinatario</label>
                    <input
                      type="email"
                      value={dailyReportConfig.email}
                      onChange={(e) => setDailyReportConfig(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="admin@rocam.com.ar"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-neutral-500 focus:border-neutral-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telfono WhatsApp</label>
                    <input
                      type="tel"
                      value={dailyReportConfig.phone}
                      onChange={(e) => setDailyReportConfig(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="3416858698"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-neutral-500 focus:border-neutral-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Solo nmero sin cdigo de pas (se agrega +549 automticamente)</p>
                  </div>
                </div>

                {/* Configuración de horarios */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hora de envo (Lun-Vie)</label>
                    <input
                      type="time"
                      value={dailyReportConfig.send_time}
                      onChange={(e) => setDailyReportConfig(prev => ({ ...prev, send_time: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-neutral-500 focus:border-neutral-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hora de envo (Sbados)</label>
                    <input
                      type="time"
                      value={dailyReportConfig.saturday_send_time}
                      onChange={(e) => setDailyReportConfig(prev => ({ ...prev, saturday_send_time: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-neutral-500 focus:border-neutral-500"
                    />
                  </div>
                </div>

                {/* Configuración de contenido */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Contenido del reporte</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dailyReportConfig.include_cash_summary}
                        onChange={(e) => setDailyReportConfig(prev => ({ ...prev, include_cash_summary: e.target.checked }))}
                        className="form-checkbox h-4 w-4 text-black dark:text-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Resumen de caja</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dailyReportConfig.include_services}
                        onChange={(e) => setDailyReportConfig(prev => ({ ...prev, include_services: e.target.checked }))}
                        className="form-checkbox h-4 w-4 text-black dark:text-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Servicios realizados</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dailyReportConfig.include_products}
                        onChange={(e) => setDailyReportConfig(prev => ({ ...prev, include_products: e.target.checked }))}
                        className="form-checkbox h-4 w-4 text-black dark:text-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Ventas de productos</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dailyReportConfig.include_appointments}
                        onChange={(e) => setDailyReportConfig(prev => ({ ...prev, include_appointments: e.target.checked }))}
                        className="form-checkbox h-4 w-4 text-black dark:text-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Turnos/Citas</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={dailyReportConfig.include_budgets}
                        onChange={(e) => setDailyReportConfig(prev => ({ ...prev, include_budgets: e.target.checked }))}
                        className="form-checkbox h-4 w-4 text-black dark:text-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Presupuestos</span>
                    </label>
                  </div>
                </div>

                {/* Vista previa de horarios */}
                {dailyReportConfig.enabled && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Programacin actual</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>Lunes a Viernes: {dailyReportConfig.send_time} hs</div>
                      <div>Sbados: {dailyReportConfig.saturday_send_time} hs</div>
                      <div>Domingos: Sin envo</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={sendTestDailyReport}
                disabled={!dailyReportConfig.enabled || !dailyReportConfig.email || !dailyReportConfig.phone || dailyReportLoading}
              >
                {dailyReportLoading ? 'Enviando...' : 'Enviar Prueba'}
              </Button>
              
              <div className="space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDailyReportModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={updateDailyReportConfig}
                  disabled={dailyReportLoading}
                  className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {dailyReportLoading ? 'Guardando...' : 'Guardar configuración'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Plantillas de Mensajes */}
      {showMessageTemplatesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-border flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-border text-foreground">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">Mensajes del Sistema</h3>
                  <p className="text-sm text-muted-foreground mt-1">Personaliza los mensajes automticos de WhatsApp y Email</p>
                </div>
                <button
                  onClick={() => setShowMessageTemplatesModal(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {loadingTemplates ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="border border-border bg-muted/40 rounded-lg p-4">
                    <h4 className="font-medium text-foreground mb-3">Variables Disponibles</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Haz clic en cualquier variable para agregarla al mensaje:
                    </p>
                    <div className="flex flex-wrap gap-2 max-h-56 overflow-auto pr-1">
                      {[
                        { var: '{clientName}', desc: 'Nombre del cliente' },
                        { var: '{clientPhone}', desc: 'Telfono del cliente' },
                        { var: '{clientEmail}', desc: 'Email del cliente' },
                        { var: '{vehicleBrand}', desc: 'Marca del vehículo' },
                        { var: '{vehicleModel}', desc: 'Modelo del vehículo' },
                        { var: '{vehicleYear}', desc: 'Año del vehículo' },
                        { var: '{vehiclePlate}', desc: 'Patente del vehículo' },
                        { var: '{vehicleColor}', desc: 'Color del vehículo' },
                        { var: '{vehicleKm}', desc: 'Kilometraje del vehículo' },
                        { var: '{vehicleInfo}', desc: 'Información completa del vehículo' },
                        { var: '{serviceType}', desc: 'Tipo de servicio' },
                        { var: '{productsList}', desc: 'Lista de productos y servicios con cantidades' },
                        { var: '{serviceDescription}', desc: 'Descripcin del servicio' },
                        { var: '{serviceCost}', desc: 'Costo del servicio' },
                        { var: '{totalCost}', desc: 'Costo total' },
                        { var: '{date}', desc: 'Fecha actual' },
                        { var: '{time}', desc: 'Hora actual' },
                        { var: '{appointmentDate}', desc: 'Fecha de la cita' },
                        { var: '{appointmentTime}', desc: 'Hora de la cita' },
                        { var: '{businessName}', desc: 'Nombre de la empresa' },
                        { var: '{businessAddress}', desc: 'Direccin de la empresa' },
                        { var: '{businessPhone}', desc: 'Telfono de la empresa' },
                        { var: '{bankName}', desc: 'Nombre del banco' },
                        { var: '{accountType}', desc: 'Tipo de cuenta' },
                        { var: '{accountNumber}', desc: 'CBU' },
                        { var: '{cbu}', desc: 'CBU' },
                        { var: '{alias}', desc: 'Alias de la cuenta' },
                        { var: '{cuit}', desc: 'CUIT de la empresa' },
                        { var: '{transferAccountDetails}', desc: 'Datos completos para transferencia' },
                        { var: '{budgetNumber}', desc: 'Numero de presupuesto' },
                        { var: '{invoiceNumber}', desc: 'Numero de factura' },
                        { var: '{remitoNumber}', desc: 'Numero de remito' },
                        { var: '{paymentMethod}', desc: 'Mtodo de pago' },
                        { var: '{discount}', desc: 'Descuento aplicado' },
                        { var: '{warranty}', desc: 'Garanta del servicio' }
                      ].map((item, index) => (
                        <Tip key={index} label={`Insertar: ${item.var}`}>
                        <button
                          onClick={() => insertVariable(item.var)}
                          className="inline-flex items-center px-3 py-2 rounded-md text-sm border border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                        >
                          {item.desc}
                        </button>
                        </Tip>
                      ))}
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="flex border-b border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => setActiveTab('whatsapp')}
                      className={`px-4 py-2 text-sm font-medium ${
                        activeTab === 'whatsapp'
                          ? 'border-b-2 border-foreground text-foreground'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      WhatsApp
                    </button>
                    <button
                      onClick={() => setActiveTab('email')}
                      className={`px-4 py-2 text-sm font-medium ${
                        activeTab === 'email'
                          ? 'border-b-2 border-foreground text-foreground'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      Email
                    </button>
                  </div>

                  {/* WhatsApp Templates */}
                  {activeTab === 'whatsapp' && (
                    <div className="space-y-4">
                      {Object.entries(messageTemplates).map(([key, template]) => (
                        <div key={key} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white">{template.name}</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{template.description}</p>
                              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                Variables: {template.variables?.join(', ') || 'Ninguna'}
                          </div>
                            </div>
                            <div className="flex items-center space-x-3 ml-4">
                              {/* Switch para habilitar/deshabilitar */}
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={template.enabled !== false}
                            onChange={(e) => {
                              const newTemplates = { ...messageTemplates };
                                    newTemplates[key] = { ...template, enabled: e.target.checked };
                              setMessageTemplates(newTemplates);
                            }}
                                  className="sr-only"
                                />
                                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 ${
                                  template.enabled !== false ? 'bg-black dark:bg-white' : 'bg-gray-300 dark:bg-gray-600'
                                }`}>
                                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    template.enabled !== false ? 'translate-x-6' : 'translate-x-1'
                                  }`} />
                                </div>
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                  {template.enabled !== false ? 'Activo' : 'Inactivo'}
                                </span>
                              </label>
                              {/* Botn de editar */}
                              <button
                                onClick={() => {
                                  setEditingTemplate({ key, template, type: 'whatsapp' });
                                  setShowEditTemplateModal(true);
                                }}
                                className="inline-flex items-center px-3 py-1.5 border border-border shadow-sm text-sm leading-4 font-medium rounded-md text-foreground bg-background hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-500"
                              >
                                <PencilIcon className="h-4 w-4 mr-1" />
                                Editar
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Email Templates */}
                  {activeTab === 'email' && (
                    <div className="space-y-4">
                      {Object.entries(emailTemplates).map(([key, template]) => (
                        <div key={key} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-white">{template.name}</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{template.description}</p>
                              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                Variables: {template.variables?.join(', ') || 'Ninguna'}
                          </div>
                            </div>
                            <div className="flex items-center space-x-3 ml-4">
                              {/* Switch para habilitar/deshabilitar */}
                              <label className="flex items-center">
                              <input
                                  type="checkbox"
                                  checked={template.enabled !== false}
                                onChange={(e) => {
                                  const newTemplates = { ...emailTemplates };
                                    newTemplates[key] = { ...template, enabled: e.target.checked };
                                  setEmailTemplates(newTemplates);
                                }}
                                  className="sr-only"
                                />
                                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 ${
                                  template.enabled !== false ? 'bg-black dark:bg-white' : 'bg-gray-300 dark:bg-gray-600'
                                }`}>
                                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    template.enabled !== false ? 'translate-x-6' : 'translate-x-1'
                                  }`} />
                            </div>
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                  {template.enabled !== false ? 'Activo' : 'Inactivo'}
                                </span>
                              </label>
                              {/* Botn de editar */}
                              <button
                                onClick={() => {
                                  setEditingTemplate({ key, template, type: 'email' });
                                  setShowEditTemplateModal(true);
                                }}
                                className="inline-flex items-center px-3 py-1.5 border border-border shadow-sm text-sm leading-4 font-medium rounded-md text-foreground bg-background hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-500"
                              >
                                <PencilIcon className="h-4 w-4 mr-1" />
                                Editar
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-card px-6 py-4 flex justify-end gap-3 border-t border-border sticky bottom-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowMessageTemplatesModal(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={async () => {
                  try {
                    if (activeTab === 'whatsapp') {
                      await updateMessageTemplates(messageTemplates);
                    } else {
                      await updateEmailTemplates(emailTemplates);
                    }
                  } catch (error) {
                    console.error('Error guardando plantillas:', error);
                  }
                }}
                disabled={loadingTemplates}
                className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingTemplates ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edicin de Template Individual */}
      {showEditTemplateModal && editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-border flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-border text-foreground">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold">Editar Template - {editingTemplate.template.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{editingTemplate.template.description}</p>
                </div>
                <button
                  onClick={() => {
                    setShowEditTemplateModal(false);
                    setEditingTemplate(null);
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {/* Variables Disponibles */}
                <div className="border border-border bg-muted/40 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-3">Variables Disponibles</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Haz clic en cualquier variable para agregarla al mensaje:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { var: '{clientName}', desc: 'Nombre del cliente' },
                      { var: '{clientPhone}', desc: 'Telfono del cliente' },
                      { var: '{clientEmail}', desc: 'Email del cliente' },
                      { var: '{vehicleBrand}', desc: 'Marca del vehículo' },
                      { var: '{vehicleModel}', desc: 'Modelo del vehículo' },
                      { var: '{vehicleYear}', desc: 'Año del vehículo' },
                      { var: '{vehiclePlate}', desc: 'Patente del vehículo' },
                      { var: '{vehicleColor}', desc: 'Color del vehículo' },
                      { var: '{vehicleKm}', desc: 'Kilometraje del vehículo' },
                      { var: '{vehicleInfo}', desc: 'Información completa del vehículo' },
                      { var: '{serviceType}', desc: 'Tipo de servicio' },
                      { var: '{productsList}', desc: 'Lista de productos y servicios con cantidades' },
                      { var: '{serviceDescription}', desc: 'Descripcin del servicio' },
                      { var: '{serviceCost}', desc: 'Costo del servicio' },
                      { var: '{totalCost}', desc: 'Costo total' },
                      { var: '{date}', desc: 'Fecha actual' },
                      { var: '{time}', desc: 'Hora actual' },
                      { var: '{appointmentDate}', desc: 'Fecha de la cita' },
                      { var: '{appointmentTime}', desc: 'Hora de la cita' },
                      { var: '{businessName}', desc: 'Nombre de la empresa' },
                      { var: '{businessAddress}', desc: 'Direccin de la empresa' },
                      { var: '{businessPhone}', desc: 'Telfono de la empresa' },
                      { var: '{bankName}', desc: 'Nombre del banco' },
                      { var: '{accountType}', desc: 'Tipo de cuenta' },
                        { var: '{accountNumber}', desc: 'CBU' },
                        { var: '{cbu}', desc: 'CBU' },
                      { var: '{alias}', desc: 'Alias de la cuenta' },
                      { var: '{cuit}', desc: 'CUIT de la empresa' },
                      { var: '{transferAccountDetails}', desc: 'Datos completos para transferencia' },
                      { var: '{budgetNumber}', desc: 'Numero de presupuesto' },
                      { var: '{invoiceNumber}', desc: 'Numero de factura' },
                      { var: '{remitoNumber}', desc: 'Numero de remito' },
                      { var: '{paymentMethod}', desc: 'Mtodo de pago' },
                      { var: '{discount}', desc: 'Descuento aplicado' },
                      { var: '{warranty}', desc: 'Garanta del servicio' }
                    ].map((item, index) => (
                      <Tip key={index} label={`Insertar: ${item.var}`}>
                      <button
                        onClick={() => insertVariable(item.var)}
                        className="inline-flex items-center px-3 py-2 rounded-md text-sm border border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                      >
                        {item.desc}
                      </button>
                      </Tip>
                    ))}
                  </div>
                </div>

                {/* Campos de edicin */}
                <div className="space-y-4">
                  {editingTemplate.type === 'whatsapp' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mensaje de WhatsApp
                      </label>
                      <textarea
                        value={editingTemplate.template.template || ''}
                        onChange={(e) => {
                          setEditingTemplate({
                            ...editingTemplate,
                            template: { ...editingTemplate.template, template: e.target.value }
                          });
                        }}
                        onFocus={(e) => setActiveTextareaRef(e.target)}
                        className="w-full h-40 p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-neutral-500 focus:border-transparent bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                        placeholder="Escribe tu mensaje aqu..."
                      />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Asunto del Email
                        </label>
                        <input
                          type="text"
                          value={editingTemplate.template.subject || ''}
                          onChange={(e) => {
                            setEditingTemplate({
                              ...editingTemplate,
                              template: { ...editingTemplate.template, subject: e.target.value }
                            });
                          }}
                          onFocus={(e) => setActiveTextareaRef(e.target as HTMLInputElement)}
                          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-neutral-500 focus:border-transparent bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                          placeholder="Asunto del email..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Cuerpo del Email
                        </label>
                        <textarea
                          value={editingTemplate.template.body || ''}
                          onChange={(e) => {
                            setEditingTemplate({
                              ...editingTemplate,
                              template: { ...editingTemplate.template, body: e.target.value }
                            });
                          }}
                          onFocus={(e) => setActiveTextareaRef(e.target)}
                          className="w-full h-40 p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-neutral-500 focus:border-transparent bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                          placeholder="Escribe el cuerpo del email aqu..."
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-card px-6 py-4 flex justify-end gap-3 border-t border-border sticky bottom-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditTemplateModal(false);
                  setEditingTemplate(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (editingTemplate.type === 'whatsapp') {
                    const newTemplates = { ...messageTemplates };
                    newTemplates[editingTemplate.key] = editingTemplate.template;
                    setMessageTemplates(newTemplates);
                  } else {
                    const newTemplates = { ...emailTemplates };
                    newTemplates[editingTemplate.key] = editingTemplate.template;
                    setEmailTemplates(newTemplates);
                  }
                  setShowEditTemplateModal(false);
                  setEditingTemplate(null);
                  toast.success('Template actualizado correctamente');
                }}
                className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90"
              >
                Guardar Cambios
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Configuración de Notificaciones */}
      {showNotificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-border flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-border text-foreground">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Configuración de Notificaciones</h3>
                <button
                  onClick={() => setShowNotificationModal(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex justify-between items-center mb-6">
                <h4 className="text-lg font-medium text-foreground">Notificaciones del Sistema</h4>
                <div className="flex space-x-2">
                  <button
                    onClick={createStockAlertNotification}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    Alerta de Stock
                  </button>
                  <button
                    onClick={handleCreateNotification}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90 transition-colors"
                  >
                    + Nueva Notificacin
                  </button>
                </div>
              </div>

              {/* Información de Alertas de Stock */}
              <div className="border border-border bg-muted/40 rounded-lg p-4 mb-6 text-foreground">
                <h5 className="font-medium mb-2">Alertas de Stock Automticas</h5>
                <p className="text-sm text-muted-foreground mb-3">
                  El sistema monitorea automticamente el inventario y puede notificar cuando:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Productos tengan stock bajo (menos del mnimo configurado)</li>
                  <li>Productos estn sin stock</li>
                  <li>Se necesite reabastecer inventario</li>
                </ul>
                <div className="mt-3 text-xs text-muted-foreground">
                  <strong>Tip:</strong> Usa el botn "Alerta de Stock" para crear una notificacion automtica
                </div>
              </div>

              {/* Lista de Notificaciones */}
              <div className="space-y-4">
                {notifications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>No hay notificaciones configuradas</p>
                    <p className="text-sm mt-1">Usa el botn "Alerta de Stock" para crear la primera notificacion</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                  <div key={notification.id} className="border border-border rounded-lg p-4 bg-background">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${notification.enabled ? 'bg-black dark:bg-white' : 'bg-muted-foreground'}`}></div>
                          <div>
                            <h5 className="font-medium text-foreground">{notification.name}</h5>
                            <p className="text-sm text-muted-foreground">{notification.description}</p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                              <span>Tipo: {notification.type || 'N/A'}</span>
                              <span>Programacin: {notification.schedule || 'N/A'}</span>
                              <span>Canales: {Array.isArray(notification.channels) ? notification.channels.join(', ') : 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => toggleNotification(notification.id, !notification.enabled)}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors border border-border ${
                              notification.enabled
                              ? 'bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90'
                              : 'bg-background text-foreground hover:bg-accent hover:text-accent-foreground'
                            }`}
                          >
                            {notification.enabled ? 'Activa' : 'Inactiva'}
                          </button>
                          <button
                            onClick={() => handleEditNotification(notification)}
                            className="px-3 py-1 rounded text-xs font-medium border border-border text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="px-3 py-1 rounded text-xs font-medium border border-border text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                          >
                            Eliminar
                          </button>
                        </div>
                    </div>
                  </div>
                ))
              )}
            </div>

              {/* Formulario de Notificacin */}
              {(selectedNotification || !notifications.length) && (
                <div className="mt-8 border border-border rounded-lg p-6 bg-muted/40">
                  <h4 className="text-lg font-medium text-foreground mb-4">
                    {selectedNotification ? 'Editar Notificacin' : 'Nueva Notificacin'}
                  </h4>
                  
                  <form onSubmit={handleSubmitNotification(selectedNotification ? updateNotification : createNotification)} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nombre
                        </label>
                        <input
                          type="text"
                          {...registerNotification('name', { required: 'El nombre es requerido' })}
                          className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-neutral-500"
                          placeholder="Nombre de la notificacion"
                        />
                        {notificationErrors.name && (
                          <p className="mt-1 text-sm text-destructive">{notificationErrors.name.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Tipo
                        </label>
                        <select
                          {...registerNotification('type', { required: 'El tipo es requerido' })}
                          className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-neutral-500"
                        >
                          <option value="email">Email</option>
                          <option value="whatsapp">WhatsApp</option>
                          <option value="sms">SMS</option>
                          <option value="system">Sistema</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Descripcin
                      </label>
                      <textarea
                        {...registerNotification('description')}
                        rows={3}
                        className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-neutral-500"
                        placeholder="Descripcin de la notificacion"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Programacin
                        </label>
                        <select
                          {...registerNotification('schedule', { required: 'La programacin es requerida' })}
                          className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-neutral-500"
                        >
                          <option value="immediate">Inmediata</option>
                          <option value="daily">Diaria</option>
                          <option value="weekly">Semanal</option>
                          <option value="monthly">Mensual</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Estado
                        </label>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            {...registerNotification('enabled')}
                            className="h-4 w-4 rounded border border-border text-black dark:text-white focus:ring-2 focus:ring-neutral-500"
                          />
                          <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Activa
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedNotification(null);
                          resetNotification();
                        }}
                        className="px-4 py-2 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 text-sm font-medium rounded-lg bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90 transition-colors"
                      >
                        {selectedNotification ? 'Actualizar' : 'Crear'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmacion de Eliminacion */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md mx-4 border border-border p-6">
            <div className="flex items-center mb-4">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full border border-border text-foreground">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-foreground mb-2">
                Confirmar Eliminacion
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Estas seguro de que quieres eliminar esta notificacion? Esta accin no se puede deshacer.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setNotificationToDelete(null);
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDeleteNotification}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90 transition-colors"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
            {/* Modal de Configuración de AFIP */}
      {showAfipConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Configuración de facturación AFIP (WSMTXCA)</h3>
              <button
                onClick={() => setShowAfipConfigModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitAfipConfig(onSubmitAfipConfig)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    CUIT emisor
                  </label>
                  <input
                    type="text"
                    {...registerAfipConfig('cuitEmisor')}
                    placeholder="20xxxxxxxxx"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-black focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Punto de venta
                  </label>
                  <input
                    type="text"
                    {...registerAfipConfig('puntoVenta')}
                    placeholder="0001"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-black focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-medium text-sm">Ambiente</Label>
                  <Select
                    value={watchAfipConfig('environment')}
                    onValueChange={(value) => setAfipConfigValue('environment', value as 'homologacion' | 'produccion')}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Selecciona el ambiente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="homologacion">Homologacion</SelectItem>
                      <SelectItem value="produccion">Produccion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-medium text-sm">WebService de facturacion</Label>
                  <Select
                    value={watchAfipConfig('preferredWs') || 'wsfe'}
                    onValueChange={(value) => setAfipConfigValue('preferredWs', value as 'wsfe' | 'wsmtxca')}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Selecciona el WS" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wsfe">WSFE — Solo totales (recomendado)</SelectItem>
                      <SelectItem value="wsmtxca">WSMTXCA — Con detalle de items</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    WSDL Homologacion (opcional)
                  </label>
                  <input
                    type="text"
                    {...registerAfipConfig('wsmtxcaWsdlHomologacion')}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-black focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    WSDL Produccion (opcional)
                  </label>
                  <input
                    type="text"
                    {...registerAfipConfig('wsmtxcaWsdlProduccion')}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-black focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Certificado (.pem o .crt)
                </label>
                <input
                  type="file"
                  accept=".pem,.crt,.cer"
                  {...registerAfipConfig('cert')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-black focus:border-transparent dark:bg-gray-700 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-200"
                />
                {afipConfig?.hasCert && (
                  <p className="mt-1 text-xs text-muted-foreground">Certificado cargado.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Clave privada (.key)
                </label>
                <input
                  type="file"
                  accept=".key"
                  {...registerAfipConfig('key')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-black focus:border-transparent dark:bg-gray-700 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 dark:file:bg-gray-700 dark:file:text-gray-200"
                />
                {afipConfig?.hasKey && (
                  <p className="mt-1 text-xs text-muted-foreground">Clave cargada.</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...registerAfipConfig('enabled')}
                  className="h-4 w-4 text-gray-700 focus:ring-black border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Habilitar facturación AFIP (WSMTXCA)
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowAfipConfigModal(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={afipConfigLoading}
                  className="px-6 py-2 text-sm font-medium rounded-lg bg-black dark:bg-white text-white hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {afipConfigLoading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal de Descuentos Personalizados */}
      {showCustomDiscountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingCustomDiscount ? 'Editar Descuento Personalizado' : 'Gestionar Descuentos Personalizados'}
              </h2>
              <button 
                onClick={() => setShowCustomDiscountModal(false)} 
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Formulario */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg max-h-[80vh] overflow-y-auto">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {editingCustomDiscount ? 'Editar Descuento' : 'Nuevo Descuento Personalizado'}
                </h3>
                
                <form onSubmit={editingCustomDiscount ? handleSubmitCustomDiscount(updateCustomDiscount) : handleSubmitCustomDiscount(createCustomDiscount)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        {...registerCustomDiscount('name', { required: 'Nombre es requerido' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                        placeholder="Ej: Descuento Lanzamiento"
                      />
                      {customDiscountErrors.name && (
                        <p className="text-red-500 text-sm mt-1">{customDiscountErrors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tipo de Descuento *
                      </label>
                      <select
                        {...registerCustomDiscount('discount_type', { required: 'Tipo es requerido' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                      >
                        <option value="percentage">Porcentaje (%)</option>
                        <option value="fixed">Monto Fijo ($)</option>
                      </select>
                      {customDiscountErrors.discount_type && (
                        <p className="text-red-500 text-sm mt-1">{customDiscountErrors.discount_type.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Valor del Descuento *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        {...registerCustomDiscount('discount_value', { required: 'Valor es requerido' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                        placeholder="Ej: 20 para 20% o $5000"
                      />
                      {customDiscountErrors.discount_value && (
                        <p className="text-red-500 text-sm mt-1">{customDiscountErrors.discount_value.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Monto Mnimo
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        {...registerCustomDiscount('min_amount')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                        placeholder="Monto mnimo para aplicar"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Descuento Mximo
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        {...registerCustomDiscount('max_discount')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                        placeholder="Lmite del descuento"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Prioridad
                      </label>
                      <input
                        type="number"
                        {...registerCustomDiscount('priority')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                        placeholder="0 = ms alta prioridad"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Descripcin
                    </label>
                    <textarea
                      {...registerCustomDiscount('description')}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                      placeholder="Descripcin del descuento"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Aplicar a *
                      </label>
                      <select
                        {...registerCustomDiscount('applies_to', { required: 'Alcance es requerido' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                      >
                        <option value="all">Todos los productos/servicios</option>
                        <option value="products">Solo productos</option>
                        <option value="services">Solo servicios</option>
                        <option value="specific_category">Categora especfica</option>
                      </select>
                      {customDiscountErrors.applies_to && (
                        <p className="text-red-500 text-sm mt-1">{customDiscountErrors.applies_to.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Categora/Producto
                      </label>
                      <input
                        type="text"
                        {...registerCustomDiscount('target_name')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                        placeholder="Ej: Alineacin, Neumticos"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Fecha de Inicio
                      </label>
                      <Controller
                        name="start_date"
                        control={customDiscountControl}
                        rules={{ required: 'Fecha de inicio es requerida' }}
                        render={({ field }) => (
                          <DatePicker
                            value={field.value || ''}
                            onChange={field.onChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                          />
                        )}
                      />
                      {customDiscountErrors.start_date && (
                        <p className="text-red-500 text-sm mt-1">{customDiscountErrors.start_date.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Fecha de Fin
                      </label>
                      <Controller
                        name="end_date"
                        control={customDiscountControl}
                        rules={{ required: 'Fecha de fin es requerida' }}
                        render={({ field }) => (
                          <DatePicker
                            value={field.value || ''}
                            onChange={field.onChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                          />
                        )}
                      />
                      {customDiscountErrors.end_date && (
                        <p className="text-red-500 text-sm mt-1">{customDiscountErrors.end_date.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Usos Mximos
                    </label>
                    <input
                      type="number"
                      {...registerCustomDiscount('max_uses')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                      placeholder="0 = sin lmite"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCustomDiscountModal(false)}
                      className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 text-sm font-medium rounded-lg bg-black dark:bg-white text-white hover:bg-green-700 transition-colors"
                    >
                      {editingCustomDiscount ? 'Actualizar' : 'Crear'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Lista de descuentos */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg max-h-[80vh] overflow-y-auto">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Descuentos Configurados
                </h3>
                
                {loadingCustomDiscounts ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                    <p className="text-gray-500 dark:text-gray-400 mt-2">Cargando...</p>
                  </div>
                ) : customDiscounts.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">No hay descuentos personalizados configurados</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {customDiscounts.map((discount) => (
                      <div key={discount.id} className="bg-white dark:bg-gray-600 p-3 rounded-lg border border-gray-200 dark:border-gray-500">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium text-gray-900 dark:text-white">{discount.name}</h4>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                discount.enabled 
                                  ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                                  : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                              }`}>
                                {discount.enabled ? 'Activo' : 'Inactivo'}
                              </span>
                              {discount.priority > 0 && (
                                <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 rounded-full">
                                  P{discount.priority}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {discount.description || 'Sin descripcin'}
                            </p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                              <span className={`font-medium ${
                                discount.discount_type === 'percentage' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'
                              }`}>
                                {discount.discount_type === 'percentage' ? `${discount.discount_value}%` : `$${discount.discount_value}`}
                              </span>
                              <span>Min: ${discount.min_amount}</span>
                              <span>Aplica a: {discount.applies_to === 'all' ? 'Todo' : discount.applies_to === 'products' ? 'Productos' : discount.applies_to === 'services' ? 'Servicios' : discount.target_name}</span>
                            </div>
                            <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                              <span>Vlido: {new Date(discount.start_date).toLocaleDateString()} - {new Date(discount.end_date).toLocaleDateString()}</span>
                              <span>Usos: {discount.current_uses}/{discount.max_uses || '8'}</span>
                            </div>
                          </div>
                          
                          <div className="flex space-x-2">
                            <Tip label="Editar">
                            <button
                              onClick={() => handleEditCustomDiscount(discount)}
                              className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            </Tip>
                            <Tip label={discount.enabled ? 'Desactivar' : 'Activar'}>
                            <button
                              onClick={() => toggleCustomDiscount(discount.id!, !discount.enabled)}
                              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                              style={{
                                backgroundColor: discount.enabled ? '#10b981' : '#d1d5db'
                              }}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  discount.enabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            </Tip>
                            <Tip label="Eliminar">
                            <button
                              onClick={() => deleteCustomDiscount(discount.id!)}
                              className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                            </Tip>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cupones */}
      {showCouponsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingCoupon ? 'Editar Cupón' : 'Gestionar Cupones de Descuento'}
              </h2>
              <button 
                onClick={() => setShowCouponsModal(false)} 
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Formulario */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg max-h-[80vh] overflow-y-auto">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {editingCoupon ? 'Editar Cupón' : 'Nuevo Cupón'}
                </h3>
                
                <form onSubmit={handleSubmitCoupon(editingCoupon ? updateCoupon : createCoupon)} className="space-y-4">
                  {/* Cdigo del cupn */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Código del Cupón *
                    </label>
                    <input
                      {...registerCoupon('code', { 
                        required: 'El cdigo es requerido',
                        pattern: {
                          value: /^[A-Z0-9_-]+$/,
                          message: 'Solo letras maysculas, nmeros, guiones y guiones bajos'
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                      placeholder="DESCUENTO20"
                      style={{ textTransform: 'uppercase' }}
                    />
                    {couponErrors.code && (
                      <p className="text-red-500 text-xs mt-1">{couponErrors.code.message}</p>
                    )}
                  </div>

                  {/* Nombre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nombre del Cupón *
                    </label>
                    <input
                      {...registerCoupon('name', { required: 'El nombre es requerido' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                      placeholder="Descuento de Bienvenida"
                    />
                    {couponErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{couponErrors.name.message}</p>
                    )}
                  </div>

                  {/* Descripcin */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Descripcin
                    </label>
                    <textarea
                      {...registerCoupon('description')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                      rows={3}
                      placeholder="Descripcin del cupn..."
                    />
                  </div>

                  {/* Tipo y valor del descuento */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tipo de Descuento *
                      </label>
                      <select
                        {...registerCoupon('discount_type', { required: 'El tipo es requerido' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                      >
                        <option value="percentage">Porcentaje (%)</option>
                        <option value="fixed">Monto Fijo ($)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Valor del Descuento *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        {...registerCoupon('discount_value', { 
                          required: 'El valor es requerido',
                          min: { value: 0.01, message: 'El valor debe ser mayor a 0' }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        placeholder="20"
                      />
                      {couponErrors.discount_value && (
                        <p className="text-red-500 text-xs mt-1">{couponErrors.discount_value.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Monto mnimo y descuento mximo */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Monto Mnimo ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        {...registerCoupon('min_amount')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        placeholder="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Descuento Mximo ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        {...registerCoupon('max_discount')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        placeholder="Sin lmite"
                      />
                    </div>
                  </div>

                  {/* Aplica a */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Aplica a *
                    </label>
                    <select
                      {...registerCoupon('applies_to', { required: 'Este campo es requerido' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                    >
                      <option value="all">Todos los productos y servicios</option>
                      <option value="products">Solo productos</option>
                      <option value="services">Solo servicios</option>
                    </select>
                  </div>

                  {/* Fechas */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Fecha de Inicio
                      </label>
                      <Controller
                        name="start_date"
                        control={couponControl}
                        render={({ field }) => (
                          <DatePicker
                            value={field.value || ''}
                            onChange={field.onChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                          />
                        )}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Fecha de Expiracin
                      </label>
                      <Controller
                        name="end_date"
                        control={couponControl}
                        render={({ field }) => (
                          <DatePicker
                            value={field.value || ''}
                            onChange={field.onChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Lmites de uso */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Mximo Total de Usos
                      </label>
                      <input
                        type="number"
                        {...registerCoupon('max_uses')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        placeholder="100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Usos por Cliente
                      </label>
                      <input
                        type="number"
                        {...registerCoupon('max_uses_per_client')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        placeholder="1"
                      />
                    </div>
                  </div>

                  {/* Botones */}
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {editingCoupon ? 'Actualizar Cupón' : 'Crear Cupón'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingCoupon(null);
                        resetCoupon();
                      }}
                      className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                      Limpiar
                    </button>
                  </div>
                </form>
              </div>

              {/* Lista de cupones */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Cupones Existentes ({coupons.length})
                  </h3>
                </div>

                {loadingCoupons ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : coupons.length === 0 ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No hay cupones configurados
                  </div>
                ) : (
                  <div className="space-y-3">
                    {coupons.map((coupon) => (
                      <div key={coupon.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-mono rounded">
                                {coupon.code}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded ${
                                coupon.status === 'active' ? 'bg-green-100 text-green-800' :
                                coupon.status === 'expired' ? 'bg-red-100 text-red-800' :
                                coupon.status === 'exhausted' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {coupon.status === 'active' ? 'Activo' :
                                 coupon.status === 'expired' ? 'Expirado' :
                                 coupon.status === 'exhausted' ? 'Agotado' : 'Desactivado'}
                              </span>
                            </div>
                            <h4 className="font-medium text-gray-900 dark:text-white">{coupon.name}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{coupon.description}</p>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 space-y-1">
                              <div>Descuento: {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`}</div>
                              <div>Usos: {coupon.current_uses}/{coupon.max_uses || '8'}</div>
                              <div>Vlido hasta: {coupon.end_date}</div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <Tip label="Editar">
                            <button
                              onClick={() => handleEditCoupon(coupon)}
                              className="p-2 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            </Tip>
                            <Tip label={coupon.enabled ? 'Desactivar' : 'Activar'}>
                            <button
                              onClick={() => toggleCoupon(coupon.id!, !coupon.enabled)}
                              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                              style={{
                                backgroundColor: coupon.enabled ? '#10b981' : '#d1d5db'
                              }}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  coupon.enabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            </Tip>
                            <Tip label="Eliminar">
                            <button
                              onClick={() => deleteCoupon(coupon.id!)}
                              className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                            </Tip>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Settings;


